import * as React from 'react';
import { FiChevronDown, FiGitBranch } from 'react-icons/fi';
import { Button } from './button';
import { cn } from '@/ui/design-system/utils/cn';

/**
 * ThinkingSteps Component
 *
 * Displays AI thinking progress as a vertical timeline with expandable/collapsible steps.
 * Based on Figma design specifications for AI story generation progress.
 */

const ThinkingSteps = React.forwardRef(
    (
        { className, steps = [], isCollapsed = false, onToggle, ...props },
        ref,
    ) => {
        return (
            <div
                ref={ref}
                className={cn(
                    'flex flex-col items-start gap-2 w-full my-4',
                    className,
                )}
                {...props}
            >
                {/* Toggle Button */}
                {onToggle && (
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={onToggle}
                        className="gap-2 text-neutral-500 hover:text-neutral-700"
                    >
                        <FiGitBranch className="w-4 h-4" />
                        <span className="text-xs font-medium leading-4">
                            Assistant steps
                        </span>
                        <FiChevronDown
                            className={cn(
                                'w-4 h-4 transition-transform',
                                isCollapsed && 'rotate-180',
                            )}
                        />
                    </Button>
                )}

                {/* Steps Container - Full width */}
                {!isCollapsed && (
                    <div className="flex flex-col items-start gap-2.5 w-full bg-white border border-gray-100 rounded-[10px] px-4 py-4 ml-2">
                        <div className="flex flex-col items-start gap-0 w-full">
                            {steps.map((step, index) => (
                                <ThinkingStep
                                    key={index}
                                    step={step}
                                    isFirst={index === 0}
                                    isLast={index === steps.length - 1}
                                    isSecondToLast={index === steps.length - 2}
                                />
                            ))}
                        </div>
                    </div>
                )}
            </div>
        );
    },
);
ThinkingSteps.displayName = 'ThinkingSteps';

const ThinkingStep = ({ step, isFirst, isLast, isSecondToLast }) => {
    const { label, status, duration } = step;

    // status can be: 'completed', 'in-progress', 'pending'
    const isCompleted = status === 'completed';
    const isInProgress = status === 'in-progress';
    const isPending = status === 'pending';

    return (
        <div className="flex flex-row justify-between items-start gap-2 w-full">
            {/* Left side: Icon + Label */}
            <div
                className={cn(
                    'flex flex-row items-center gap-2 flex-1',
                    isInProgress && 'animate-pulse',
                )}
            >
                {/* Timeline Icon Container */}
                <div className="relative flex flex-col items-center w-4">
                    {/* Top connector line - for all items except the first */}
                    {!isFirst && !isLast && (
                        <div
                            className="absolute w-0.5 bg-gray-200"
                            style={{
                                left: 'calc(50% - 1px)',
                                top: 0,
                                height: '18px',
                            }}
                        />
                    )}

                    {/* Status indicator dot */}
                    <div
                        className={cn(
                            'w-2 h-2 rounded-full z-10 relative',
                            isCompleted && 'bg-gray-200',
                            isInProgress && 'bg-gray-200', // Add pulse animation for in-progress
                            isPending && 'bg-white border border-gray-200',
                        )}
                    />

                    {/* Bottom connector line - for all items except the last */}
                    {!isLast && (
                        <div
                            className="absolute w-0.5 bg-gray-200"
                            style={{
                                left: 'calc(50% - 1px)',
                                top: '8px',
                                height: isSecondToLast ? '14px' : '18px',
                            }}
                        />
                    )}
                </div>

                {/* Label */}
                <div
                    className={cn(
                        'flex items-center text-xs font-normal leading-4 flex-1',
                        // Apply different colors based on status
                        isCompleted && 'text-neutral-500',
                        isInProgress && 'text-neutral-500',
                        isPending && 'text-muted-foreground opacity-30', // Use muted-foreground for pending
                    )}
                >
                    {label}
                </div>
            </div>

            {/* Right side: Duration/Status */}
            <div
                className={cn(
                    'text-xs font-normal leading-4 whitespace-nowrap',
                    // Apply different colors based on status
                    isCompleted && 'text-neutral-500',
                    isInProgress && 'text-neutral-500',
                    isPending && 'text-muted-foreground opacity-30', // Use muted-foreground for pending
                )}
            >
                {duration}
            </div>
        </div>
    );
};

export { ThinkingSteps, ThinkingStep };
